/**
 * Field type definitions for the template editor.
 * Each field type has default dimensions, constraints, and styling.
 */

export const FIELD_TYPES = {
  signature: {
    id: 'signature',
    name: 'Signature',
    icon: 'Pen',
    // Default dimensions as percentage of page (0-1)
    defaultWidth: 0.2,
    defaultHeight: 0.05,
    minWidth: 0.1,
    minHeight: 0.03,
    // Color for visual identification
    color: '#EF4444',
    bgColor: 'rgba(239, 68, 68, 0.1)',
    borderColor: 'rgba(239, 68, 68, 0.5)',
  },
  initials: {
    id: 'initials',
    name: 'Initials',
    icon: 'Type',
    defaultWidth: 0.08,
    defaultHeight: 0.04,
    minWidth: 0.05,
    minHeight: 0.025,
    color: '#F97316',
    bgColor: 'rgba(249, 115, 22, 0.1)',
    borderColor: 'rgba(249, 115, 22, 0.5)',
  },
  text: {
    id: 'text',
    name: 'Text',
    icon: 'TextCursor',
    defaultWidth: 0.25,
    defaultHeight: 0.03,
    minWidth: 0.05,
    minHeight: 0.02,
    color: '#3B82F6',
    bgColor: 'rgba(59, 130, 246, 0.1)',
    borderColor: 'rgba(59, 130, 246, 0.5)',
  },
  date: {
    id: 'date',
    name: 'Date',
    icon: 'Calendar',
    defaultWidth: 0.12,
    defaultHeight: 0.03,
    minWidth: 0.08,
    minHeight: 0.02,
    color: '#8B5CF6',
    bgColor: 'rgba(139, 92, 246, 0.1)',
    borderColor: 'rgba(139, 92, 246, 0.5)',
  },
  checkbox: {
    id: 'checkbox',
    name: 'Checkbox',
    icon: 'CheckSquare',
    defaultWidth: 0.025,
    defaultHeight: 0.025,
    minWidth: 0.015,
    minHeight: 0.015,
    color: '#10B981',
    bgColor: 'rgba(16, 185, 129, 0.1)',
    borderColor: 'rgba(16, 185, 129, 0.5)',
  },
};

// Array of field types for rendering in the palette
export const FIELD_TYPE_LIST = Object.values(FIELD_TYPES);

// Default colors for submitter roles
export const ROLE_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#84CC16', // Lime
];

/**
 * Get the next available role color
 * @param {Array} existingRoles - Array of existing roles with color property
 * @returns {string} - Hex color code
 */
export const getNextRoleColor = (existingRoles) => {
  const usedColors = existingRoles.map(r => r.color);
  const availableColor = ROLE_COLORS.find(c => !usedColors.includes(c));
  return availableColor || ROLE_COLORS[existingRoles.length % ROLE_COLORS.length];
};

/**
 * Generate a unique ID for fields and roles
 * @returns {string} - UUID-like string
 */
export const generateId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Create a new field with default values
 * @param {string} type - Field type (signature, text, etc.)
 * @param {Object} position - { x, y } normalized coordinates
 * @param {number} page - Page number (1-indexed)
 * @param {string} roleUuid - UUID of the submitter role
 * @returns {Object} - New field object
 */
export const createField = (type, position, page, roleUuid) => {
  const fieldType = FIELD_TYPES[type];
  if (!fieldType) {
    throw new Error(`Unknown field type: ${type}`);
  }

  return {
    uuid: generateId(),
    name: fieldType.name,
    type: type,
    required: true,
    roleUuid: roleUuid,
    areas: [{
      x: position.x,
      y: position.y,
      w: fieldType.defaultWidth,
      h: fieldType.defaultHeight,
      page: page,
    }],
  };
};

/**
 * Create a new submitter role
 * @param {string} name - Role name
 * @param {Array} existingRoles - Existing roles for color selection
 * @returns {Object} - New role object
 */
export const createRole = (name, existingRoles = []) => {
  return {
    uuid: generateId(),
    name: name,
    color: getNextRoleColor(existingRoles),
  };
};

export default FIELD_TYPES;
